<template>
  <f7-page>
    <f7-navbar :title="title" back-link="Terug" />

    <!--
      THE WINDOW FILTER. Chantal must be able to look at any time window, not only at a
      fixed window around now. Both fields are `datetime-local`, so the browser shows and
      accepts LOCAL wall time -- the same clock the gym runs on.

      No `v-model`. The value is bound one way and the change is handled explicitly, which
      is the idiom the sibling admin screen (booking-stages.vue) uses: an f7-list-input is
      a wrapper component, and a two-way binding through it hides which event actually
      fired. `$event.target.value` reads the real input.
    -->
    <f7-list class="list-strong list-outline list-dividers">
      <f7-list-input
        label="Van"
        type="datetime-local"
        :value="from"
        @input="onWindowInput('from', $event.target.value)"
      ></f7-list-input>
      <f7-list-input
        label="Tot"
        type="datetime-local"
        :value="to"
        @input="onWindowInput('to', $event.target.value)"
      ></f7-list-input>
    </f7-list>

    <f7-list class="search-list list-strong list-outline list-dividers" v-if="data.length === 0">
      <f7-list-item title="Geen leden in dit tijdvak" />
    </f7-list>
    <f7-list class="search-list searchbar-found list-strong list-outline list-dividers" v-else>
      <f7-list-item
        v-for="training_slot in data"
        :link="'/users/' + training_slot.user.id"
        :title="training_slot.user.first_name + ' ' + training_slot.user.last_name + ' - Eiland: ' + training_slot.power_rack"
        :header="training_slot.start_date_time + ' - ' + training_slot.stop_time"
        :footer="training_slot.gym.street + ' in ' + training_slot.gym.city"
        swipeout
        @swipeout:deleted="onDeleted(training_slot)"
      >
        <f7-swipeout-actions right>
          <f7-swipeout-button
            close
            delete
            :confirm-text="confirmText(training_slot)"
          >
            Annuleer
          </f7-swipeout-button>
        </f7-swipeout-actions>
      </f7-list-item>
    </f7-list>
  </f7-page>
</template>

<script>

import ThrottlesRequests from "../../mixins/ThrottlesRequests.vue";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export default {
  components: {},

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [
    ThrottlesRequests
  ],

  data: function () {
    return {
      show_loader: true,
      title: 'Leden in de gym',
      query: '',

      // The two field values, in the `YYYY-MM-DDTHH:mm` shape a datetime-local input
      // requires. They are LOCAL wall time, never UTC. mounted() fills them.
      from: '',
      to: '',

      data: []
    };
  },

  computed: {
    current_user() {
      return this.$store.state.current_user.data;
    },
    gym_id() {
      return parseInt(this.f7route.params.gym_id);
    },
  },

  created() {
    //
  },

  mounted() {
    this.from = this.toInputValue(this.defaultFrom());
    this.to = this.toInputValue(this.defaultTo());
    this.fetchUsers();
  },

  methods: {
    confirmText(trainingSlot) {
      return 'Weet je zeker dat je de reservering op <strong><em>' + trainingSlot.start_date + '</em></strong> van <strong><em>' + trainingSlot.start_time + 'tot' + trainingSlot.stop_time + '</strong></em> aan de <strong><em>' + trainingSlot.gym.street + '</em></strong> in <strong><em>' + trainingSlot.gym.city + '</em></strong> wil annuleren?';
    },

    /**
     * A Date as the `YYYY-MM-DDTHH:mm` a datetime-local input shows.
     *
     * Built from the local getters on purpose. `toISOString()` would return UTC, and the
     * admin would read a wall time one or two hours away from the clock on the wall.
     */
    toInputValue(date) {
      const pad = (value) => String(value).padStart(2, '0');

      return date.getFullYear()
        + '-' + pad(date.getMonth() + 1)
        + '-' + pad(date.getDate())
        + 'T' + pad(date.getHours())
        + ':' + pad(date.getMinutes());
    },

    defaultFrom() {
      return new Date();
    },

    defaultTo() {
      return new Date(Date.now() + ONE_DAY_MS);
    },

    /** One field as a Date, or null when it is empty or unparsable. */
    parseField(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);

      return isNaN(date.getTime()) ? null : date;
    },

    /**
     * The window as the API should receive it.
     *
     * A CLEARED FIELD FALLS BACK ON THE OTHER FIELD, not on the clock. Measured in the
     * browser on 2026-09-18: with a fallback fixed at now(), an admin who was looking at
     * yesterday and then cleared "Van" sent from=now with to=yesterday 23:59. The API
     * answered 422 (`to` must be after_or_equal `from`) and the page showed nothing. That
     * is a correct API and a broken page.
     *
     * Anchoring on the other end keeps the window coherent whatever the admin clears: one
     * cleared field means "a 24 hour window measured from the end I left alone". Only when
     * BOTH ends are empty does the clock decide, which is the same window the page opens on.
     */
    windowForApi() {
      const from = this.parseField(this.from);
      const to = this.parseField(this.to);

      return {
        from: this.toApiValue(
          this.from,
          to ? new Date(to.getTime() - ONE_DAY_MS) : this.defaultFrom()
        ),
        to: this.toApiValue(
          this.to,
          from ? new Date(from.getTime() + ONE_DAY_MS) : this.defaultTo()
        ),
      };
    },

    /**
     * One field value as the API should receive it.
     *
     * An empty or unparsable field falls back to its default rather than travelling to the
     * server as '' or as `Invalid Date`. Clearing a field must not break the page.
     *
     * `toISOString()` is right HERE, unlike in toInputValue above: the browser reads a
     * datetime-local string as LOCAL time, so the Date already holds the instant the admin
     * meant, and the `Z` form states that instant unambiguously. The API converts it back
     * to Europe/Amsterdam before it compares.
     */
    toApiValue(value, fallbackDate) {
      let date = value ? new Date(value) : null;

      if (!date || isNaN(date.getTime())) {
        date = fallbackDate;
      }

      return date.toISOString();
    },

    /**
     * No extra debounce. The ThrottlesRequests mixin already waits 300 ms and drops every
     * superseded call, so typing into a datetime field fires one request, not six.
     */
    onWindowInput(field, value) {
      this[field] = value;
      this.fetchUsers();
    },

    fetchUsers() {
      this.loading = true;
      this.dispatchThrottled("api/getEndpoint", {
        endpoint: '/gyms/' + this.gym_id + '/current-users',
        data: this.windowForApi(),
      })
        .then((response) => {
          this.data = response.data.data;
        })
        // The mixin REJECTS on a failed request and the store has already raised its own
        // danger toast. Without this catch the rejection is unhandled, which the browser
        // reports as a page error on top of the toast. Measured 2026-09-18 on a 422.
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    onDeleted(trainingSlot) {
      this.$store
        .dispatch("training_slots/destroy", {
          training_slot: trainingSlot,
        })
        .then(() => {
          this.$store.dispatch("notifications/addSuccessNotification", {
            message: "Reservering geannuleerd",
          });
        })
        .catch(() => {});
    },
  },

  watch: {
    loading(loading) {
      if (loading) {
        this.$store.commit('app/show_loader');
      } else {
        this.$store.commit('app/hide_loader');
      }
    }
  }
};
</script>
